/**
 * La card de un canal de WhatsApp Business (Coexistence) en su ficha (tareas 3.4 y 3.5): conectar el
 * número con Facebook, lo que Meta dice de él, y el paso del método de pago.
 *
 * Reemplaza a la card del QR: un canal de la Cloud API no tiene sesión que escanear, lo conecta el
 * Embedded Signup de Meta (D29) y lo desconecta Meta (D15).
 */
import { useState, type ReactNode } from "react";
import { Alert, Button, Card, Checkbox, Descriptions, Flex, Progress, Space, Spin, Tooltip, Typography, message } from "antd";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  ExclamationCircleFilled,
  FacebookFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Channel, ChannelConnectionStatus, conversationsApi } from "@/services/conversations.api";
import { ParteDelSync, estadoCloud, estadoDelSync, whatsappManagerDe } from "./cloud-channel";
import { mensajeDelAlta } from "./embedded-signup";
import { useEmbeddedSignup } from "./useEmbeddedSignup";

const { Paragraph, Text, Link } = Typography;

export const CloudChannelCard = ({
  channel,
  status,
  onChannel,
}: {
  channel: Channel;
  /** El estado en vivo, que llega por el socket antes que el canal recargado. */
  status: ChannelConnectionStatus;
  /** El canal como quedó después de conectar o de confirmar el pago. */
  onChannel: (channel: Channel) => void;
}) => {
  const { estado, conectar } = useEmbeddedSignup(channel.channelId, (ch) => {
    message.success("Número conectado");
    onChannel(ch);
  });
  const info = estadoCloud({ config: channel.config, connectionStatus: status });
  const sync = estadoDelSync(channel.config);
  const ocupado = estado.fase === "popup" || estado.fase === "conectando";
  const puedeAbrir = estado.fase === "lista" || estado.fase === "error";

  const botonConectar = (texto: string) => (
    <Button
      type="primary"
      icon={<FacebookFilled />}
      onClick={conectar}
      disabled={!puedeAbrir}
      loading={ocupado}
    >
      {texto}
    </Button>
  );

  /** Lo que pasa con el flujo de Meta mientras está abierto, o cómo terminó. */
  const estadoDelFlujo = (
    <>
      {estado.fase === "cargando" && <Spin size="small" tip="Cargando Facebook…" />}
      {estado.fase === "no_disponible" && (
        <Alert type="warning" showIcon message="No se puede conectar desde acá" description={estado.motivo} />
      )}
      {estado.fase === "popup" && (
        <Text type="secondary">
          Seguí los pasos en la ventana de Facebook. Si no se abrió, fijate que el navegador no la haya
          bloqueado.
        </Text>
      )}
      {estado.fase === "conectando" && <Spin size="small" tip="Conectando el número…" />}
      {estado.fase === "error" && (
        <Alert type="error" showIcon message="No se pudo conectar el número" description={estado.motivo} />
      )}
      {estado.fase === "lista" && estado.motivo && <Text type="secondary">{estado.motivo}</Text>}
    </>
  );

  return (
    <Card
      title={<span style={{ fontSize: 14 }}>Conectá tu WhatsApp Business</span>}
      // El mismo borde que las tablas de la ficha del canal (xcons-docs#508).
      style={{ marginBottom: 16, border: "1px solid #d9d9d9" }}
      styles={{ header: { minHeight: 38, padding: "0 16px" } }}
    >
      {!info.conectadoAlgunaVez ? (
        <Flex vertical gap={12}>
          <Paragraph style={{ margin: 0 }}>
            Conectá el número de tu app <Text strong>WhatsApp Business</Text> a xcons a través de la conexión
            oficial de Meta. Vas a seguir usando el celular como siempre, y además vas a ver y responder los
            mensajes desde acá.
          </Paragraph>
          <div>
            <Text>Antes de empezar, tené a mano:</Text>
            <ul style={{ paddingLeft: 20, margin: "4px 0 0" }}>
              <li>La app WhatsApp Business actualizada, con este número activo.</li>
              <li>
                Una cuenta de Facebook que administre el negocio en Meta. Si el negocio todavía no existe en Meta,
                lo podés crear durante la conexión.
              </li>
              <li>Tu celular: Meta te va a pedir que escanees un código con la app.</li>
            </ul>
          </div>
          <Text type="secondary">
            Desde xcons sólo podés responder a quien te escribió en las últimas 24 horas. Para escribirle primero
            a alguien, usá la app del celular o una plantilla aprobada.
          </Text>
          {/* Sin banner (xcons-docs#508): el aviso va como texto warning, con ícono para no depender del color. */}
          <Text strong italic type="warning">
            <ExclamationCircleFilled style={{ marginRight: 6 }} />
            Al conectarlo se cierran los dispositivos vinculados al número. Si está conectado por QR en otro canal,
            ese canal se desconecta. No lo vuelvas a escanear por QR después: los mensajes llegarían dos veces.
          </Text>
          <Space direction="vertical" size={8}>
            {botonConectar("Conectar con Facebook")}
            {estadoDelFlujo}
          </Space>
        </Flex>
      ) : (
        <Flex vertical gap={12}>
          {/* Todo en una línea y del mismo tamaño (#519): la etiqueta más larga es «Importación del celular». */}
          <Descriptions
            size="small"
            column={1}
            colon={false}
            labelStyle={{ width: 200, whiteSpace: "nowrap" }}
            contentStyle={{ whiteSpace: "nowrap" }}
          >
            <Descriptions.Item label="Número">{info.numero ?? channel.identifier}</Descriptions.Item>
            <Descriptions.Item label="Nombre en WhatsApp">{info.nombreVerificado ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Calidad">
              {info.calidad ? (
                <Space size={6}>
                  <span
                    style={{ width: 8, height: 8, borderRadius: "50%", background: info.calidad.color, display: "inline-block" }}
                  />
                  {info.calidad.texto}
                </Space>
              ) : (
                <Text type="secondary">Meta todavía no la informó</Text>
              )}
              {info.saludRevisada && (
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  (revisada el {dayjs(info.saludRevisada).format("DD/MM HH:mm")})
                </Text>
              )}
            </Descriptions.Item>
            {sync?.resumen && (
              <Descriptions.Item
                label={
                  <Space size={4}>
                    Importación del celular
                    <Tooltip title="Se hace una sola vez, al conectar el número. Lo importado entra en conversaciones cerradas y sin no leídos, y no pasa al Historial del Contacto.">
                      <InfoCircleOutlined aria-label="Más información" style={{ cursor: "help" }} />
                    </Tooltip>
                  </Space>
                }
              >
                <SyncDelCelular resumen={sync.resumen} />
              </Descriptions.Item>
            )}
          </Descriptions>

          {info.desconexion && (
            <AvisoDelCanal
              tipo="danger"
              titulo="Meta desconectó el número"
              texto={
                <>
                  {/* El motivo ya trae su punto final: la fecha va antes. */}
                  {info.desconexion.desde && `Desde el ${dayjs(info.desconexion.desde).format("DD/MM/YYYY HH:mm")}. `}
                  {info.desconexion.motivo} Mientras tanto no entra ni sale nada por este canal. Se reconecta sobre el
                  mismo canal, sin perder las conversaciones.
                </>
              }
            >
              {botonConectar("Conectar con Facebook")}
              {estadoDelFlujo}
            </AvisoDelCanal>
          )}

          {!info.desconexion && info.avisoWebhook && (
            <AvisoDelCanal
              tipo="warning"
              titulo="Los mensajes de este número pueden no estar llegando acá"
              texto={
                <>
                  Meta está mandando los mensajes de este número a otro lugar. Conectalo de nuevo con Facebook para
                  recibirlos acá.{" "}
                  {/* El texto del servicio trae la URL del webhook: queda a mano para soporte (#520 lo reescribe). */}
                  <Tooltip title={info.avisoWebhook}>
                    <InfoCircleOutlined aria-label="Detalle técnico" style={{ cursor: "help" }} />
                  </Tooltip>
                </>
              }
            >
              {botonConectar("Conectar con Facebook")}
              {estadoDelFlujo}
            </AvisoDelCanal>
          )}

          {info.problemaDePago && (
            <ProblemaDePago channel={channel} wabaId={info.wabaId} desde={info.problemaDePago.desde} onChannel={onChannel} />
          )}

          {!info.pagoConfirmado && !info.problemaDePago && (
            <PasoDelPago channel={channel} wabaId={info.wabaId} onChannel={onChannel} />
          )}
        </Flex>
      )}
    </Card>
  );
};

const ICONO_DEL_TONO: Record<ParteDelSync["tono"], JSX.Element> = {
  ok: <CheckCircleFilled style={{ color: "#52c41a" }} />,
  espera: <ClockCircleOutlined style={{ color: "#1677ff" }} />,
  problema: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
};

/**
 * El sync inicial de la agenda y el historial del celular (tarea 5.1, D23), en una línea (#519). Sin
 * botones: lo pide el servicio solo al conectar y lo reintenta mientras dure el plazo.
 */
const SyncDelCelular = ({ resumen }: { resumen: ParteDelSync }) => (
  <div>
    <Space size={6} align="start">
      {ICONO_DEL_TONO[resumen.tono]}
      <span>{resumen.texto}</span>
    </Space>
    {resumen.progreso != null && <Progress percent={resumen.progreso} size="small" style={{ maxWidth: 320, marginLeft: 20 }} />}
  </div>
);

/**
 * Un aviso de la ficha (#519): una línea, del tamaño de los datos del canal, dentro de un recuadro con el borde del tono,
 * con ícono para no depender del color, y la acción debajo.
 */
const AvisoDelCanal = ({
  tipo,
  titulo,
  texto,
  children,
}: {
  tipo: "warning" | "danger";
  titulo: string;
  texto: ReactNode;
  children: ReactNode;
}) => (
  // El borde de los avisos de siempre, con un fondo más suave y el texto en el negro de siempre: el
  // color queda en el borde y el ícono.
  <Flex
    vertical
    gap={8}
    align="flex-start"
    style={{ border: `1px solid ${COLORES_DEL_AVISO[tipo].borde}`, background: COLORES_DEL_AVISO[tipo].fondo, borderRadius: 8, padding: "8px 12px" }}
  >
    <Text>
      <ExclamationCircleFilled style={{ marginRight: 6, color: COLORES_DEL_AVISO[tipo].icono }} />
      <Text strong>
        {titulo}.
      </Text>{" "}
      {texto}
    </Text>
    {children}
  </Flex>
);

/** Los del `Alert` de antd en el borde y el ícono; el fondo, más claro que el suyo. */
const COLORES_DEL_AVISO = {
  warning: { borde: "#ffe58f", fondo: "#fffdf3", icono: "#faad14" },
  danger: { borde: "#ffccc7", fondo: "#fffaf9", icono: "#ff4d4f" },
};

/**
 * El paso del método de pago (D2). No se puede verificar desde acá, así que se pide confirmarlo: queda
 * a la vista hasta que un admin lo confirma, y conectar otra cuenta lo vuelve a pedir.
 */
const PasoDelPago = ({
  channel,
  wabaId,
  onChannel,
}: {
  channel: Channel;
  wabaId?: string;
  onChannel: (channel: Channel) => void;
}) => {
  const [cargado, setCargado] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const confirmar = async () => {
    setGuardando(true);
    try {
      onChannel(await conversationsApi.confirmPayment(channel.channelId));
      message.success("Método de pago confirmado");
    } catch (e) {
      message.error(`No se pudo guardar: ${mensajeDelAlta(e)}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Alert
      type="info"
      showIcon
      message="Último paso: el método de pago de la cuenta"
      description={
        <Flex vertical gap={8}>
          <span>
            Desde el <Text strong>1 de octubre de 2026</Text>, Meta cobra las respuestas que salen desde el panel
            una vez superado el cupo gratis del mes. Las paga el negocio, en su cuenta de Meta: <Text strong>sin un
            método de pago, pasado el cupo los mensajes no se entregan</Text>. Lo que se manda desde la app del
            celular sigue sin costo.
          </span>
          <Link href={whatsappManagerDe(wabaId)} target="_blank" rel="noreferrer">
            Cargarlo en WhatsApp Manager
          </Link>
          <Checkbox checked={cargado} onChange={(e) => setCargado(e.target.checked)}>
            Ya hay un método de pago cargado en la cuenta de Meta del negocio
          </Checkbox>
          <div>
            <Button type="primary" disabled={!cargado} loading={guardando} onClick={confirmar}>
              Confirmar
            </Button>
          </div>
        </Flex>
      }
    />
  );
};

/** Meta rechazó un mensaje por el método de pago (`131042`): el cartel queda hasta que alguien lo resuelve. */
const ProblemaDePago = ({
  channel,
  wabaId,
  desde,
  onChannel,
}: {
  channel: Channel;
  wabaId?: string;
  desde?: string;
  onChannel: (channel: Channel) => void;
}) => {
  const [guardando, setGuardando] = useState(false);
  const resuelto = async () => {
    setGuardando(true);
    try {
      onChannel(await conversationsApi.confirmPayment(channel.channelId));
      message.success("Listo: si Meta vuelve a rechazar un mensaje, el aviso vuelve");
    } catch (e) {
      message.error(`No se pudo guardar: ${mensajeDelAlta(e)}`);
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Alert
      type="error"
      showIcon
      message="Meta no está entregando mensajes por el método de pago"
      description={
        <Flex vertical gap={8}>
          <span>
            {desde ? `Desde el ${dayjs(desde).format("DD/MM/YYYY HH:mm")}, ` : ""}Meta rechaza las respuestas de
            este número porque la cuenta del negocio no tiene un método de pago válido. Hay que cargarlo en
            WhatsApp Manager.
          </span>
          <Link href={whatsappManagerDe(wabaId)} target="_blank" rel="noreferrer">
            Abrir WhatsApp Manager
          </Link>
          <div>
            <Button icon={<CheckCircleFilled />} loading={guardando} onClick={resuelto}>
              Ya lo cargué
            </Button>
          </div>
        </Flex>
      }
    />
  );
};
